import React, { useState,  } from 'react';
import { useForm } from 'react-hook-form';
import get from 'lodash/get';
import merge from 'lodash/merge';
import set from 'lodash/set';
import forEach from 'lodash/forEach';
import isFunction from 'lodash/isFunction';
import snakeCase from 'lodash/snakeCase';
import cx from 'classnames'
import { handleErrorWithPromise } from '../../core/axios.service';
import { postFormData } from '../../core/api-forms.service';
import { useModal } from '../../context/modal-context';
import { publicPath } from '../../core/helpers';
import MessageBox from '../message-box/message-box';

const errorClassName = (errors, fieldName) => {
    return get(errors, `${fieldName}.message`, false) ? 'mlb-is-invalid' : '';
};

const FormError = ({ errors, fieldName, className }) => {
    const errorMessage = get(errors, `${fieldName}.message`, false);
    return errorMessage ? (
        <span className={`mlb-invalid-message ${className}`}>{errorMessage}</span>
    ) : null;
};


const TextField = ({fieldName, label, register, registerOptions, translations, errors, type = 'text'}) => {
    const fieldRegisterOptions = merge({}, registerOptions);
    const fieldNameSnakeCase = snakeCase(fieldName);
    const fieldRequiredTranslation = get(translations, `required_${fieldNameSnakeCase}`)
    if (fieldRequiredTranslation) {
        set(fieldRegisterOptions, 'required.message', fieldRequiredTranslation);
    }
    return(
        <div className={cx('mlb-field-wrap', errorClassName(errors, fieldName))}>
            <input {...register(fieldName, fieldRegisterOptions)} placeholder={label} type={type} />
            <FormError errors={errors} fieldName={fieldName} />
        </div>
    );
}

const RequestForm = ({ translations, type = 'offer' }) => {

    const [loading, setLoading] = useState(false);
    const { setModal, setModalOptions } = useModal();

    const {
        register,
        formState: { errors },
        handleSubmit,
        getFieldState,
        getValues,
        reset
    } = useForm({
        mode: 'onTouched'
    });


    const closeModal = () => {
        setModalOptions({size: 'default'});
        setModal();
    }

    const handleError = (e) => {
        reset();
        setModalOptions({size: 'small'});
        setModal(<MessageBox translations={translations} status='error' />);
        handleErrorWithPromise(e);
    }

    const onSubmit = data => {
        setLoading(true);
        const resultData = {
            campaign: "",
            origin: "",
            gaClientID: "",
            gaTrackID: "",
            marketingAgreementText: true
        };

        const ga = window.ga || false;
        if (ga) {
            const tracker = ga.getAll()[0];
            if (isFunction(tracker.get)) {
                resultData.gaClientID = tracker.get('clientId');
                resultData.gaTrackID = tracker.get('trackingId');
            }
        }

        const url = new URL(window.location.href);
        const campaign = url.searchParams.get("campaign");
        const utm_campaign = url.searchParams.get("utm_campaign");
        const origin = url.searchParams.get("origin");

        if (campaign) {
            resultData.campaign = campaign;
        } else if (utm_campaign) {
            resultData.campaign = utm_campaign;
        }

        if (origin) {
            resultData.origin = origin;
        }

        forEach(Object.keys(data), key => {
            resultData[key] = data[key];
        });

        postFormData(resultData, type).then((res) => {
            if (get(res, 'data.success') === true) {
                reset();
                setModalOptions({size: 'small'});
                setModal(<MessageBox translations={translations} status='success' />);
            }
        })
        .catch(handleError);
    }

    const t = key => get(translations, key, '-');
    const textFieldProps = { register, errors, getFieldState, getValues, translations, registerOptions: { required: { value: true, message: t('required') } } };

    return (
        <div className={cx('mlb-request-form', `mlb-request-form--${type}`)}>
            <button className={cx('mlb-request-form_close')} onClick={closeModal}>
                <svg height="30" width="30" viewBox="0 0 30 30" aria-hidden="true" focusable="false">
                    <path d="M14.2504 15L8 8.74956L8.74956 8L15 14.2504L21.2504 8L22 8.74956L15.7496 15L22 21.2504L21.2504 22L15 15.7496L8.74956 22L8 21.2504L14.2504 15Z"/>
                </svg>
            </button>
            <div className='mlb-request-form_banner-wrap' >
                <img src={publicPath(`static/images/form-s-${type}.jpg`)} alt='' className='mlb-request-form_banner' />
            </div>
            <form onSubmit={handleSubmit(onSubmit)} className={cx({'mlb-is-loading': loading})}>
                <p className={cx('mlb-request-form_heading')}>
                    {t(`form_heading_${snakeCase(type)}`)}
                </p>
                <div className={cx('mlb-field-group')}>
                    <div className={cx('mlb-field-group_desc')}>
                        <strong>{t('personal_data')}</strong>
                    </div>
                    <div className={cx('mlb-field-group_cols')}>
                        <div className={cx('mlb-field-group_col')}>
                            <TextField { ...textFieldProps } fieldName='firstName' label={`${t('first_name')} *`} registerOptions={{
                                required: { value: true, message: t('required') },
                                maxLength: {
                                    value: 40,
                                    message: t('first_name_to_long')
                                }
                            }}/>
                        </div>
                        <div className={cx('mlb-field-group_col')}>
                            <TextField { ...textFieldProps } fieldName='lastName' label={`${t('last_name')} *`} registerOptions={{
                                required: { value: true, message: t('required') },
                                maxLength: {
                                    value: 80,
                                    message: t('last_name_to_long')
                                }
                            }}/>
                        </div>
                    </div>
                </div>
                <div className={cx('mlb-field-group')}>
                    <div className={cx('mlb-field-group_desc')}>
                        <strong>{t('contact_data')}</strong>
                    </div>
                    <div className={cx('mlb-field-group_cols')}>
                        <div className={cx('mlb-field-group_col')}>
                            <TextField { ...textFieldProps } fieldName='email' label={`${t('email')} *`} registerOptions={{
                                required: { value: true, message: t('required') },
                                pattern: {
                                    value: /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                                    message: t('required_e_mail')
                                }
                            }}/>
                        </div>
                        <div className={cx('mlb-field-group_col')}>
                            <TextField { ...textFieldProps } fieldName='phone' label={`${t('phone')} *`} type='number' registerOptions={{
                                required: { value: true, message: t('required') },
                                pattern: { value: new RegExp('^[+0-9][0-9 ]{8,15}$'), message: t('required_phone') }
                            }}/>
                        </div>
                    </div>
                </div>
                <div className={cx('mlb-field-group')}>
                    <div className={cx('mlb-field-group_desc')}>
                        <strong>{t('message_data')}</strong>
                    </div>
                    <div className={cx('mlb-field-group_cols')}>
                        <div className={cx('mlb-field-group_col')}>
                            <div className={cx('mlb-field-wrap mlb-textarea-wrap', errorClassName(errors, 'other'))}>
                                <textarea {...register('message')} placeholder={t('message')} />
                                <FormError errors={errors} fieldName='message' />
                            </div>
                        </div>
                    </div>
                </div>
                <div className={cx('mlb-field-group')}>
                    <div className={cx('mlb-field-group_desc')} />
                    <div className={cx('mlb-field-group_cols')}>
                        <div className={cx('mlb-field-group_col')}>
                            <div className='mlb-margin-bottom'>
                                <p>{t(`legal_info_${snakeCase(type)}`)}</p>
                            </div>
                            <div className={cx('mlb-field-wrap mlb-checkbox-wrap', errorClassName(errors, 'emailAllowed'))}>
                                <label className='mlb-checkbox-field' htmlFor='emailAllowed'>
                                    <input type='checkbox' {...register('emailAllowed')} id='emailAllowed' />
                                    <span className='mlb-checkmark' />
                                    {t('marketing_info_email')}
                                </label>
                                <FormError errors={errors} fieldName='optInLegalCompetition' />
                            </div>
                            <div className={cx('mlb-field-wrap mlb-checkbox-wrap', errorClassName(errors, 'phoneCallsAllowed'))}>
                                <label className='mlb-checkbox-field' htmlFor='phoneCallsAllowed'>
                                    <input type='checkbox' {...register('phoneCallsAllowed')} id='phoneCallsAllowed' />
                                    <span className='mlb-checkmark' />
                                    {t('marketing_info_phone')}
                                </label>
                                <FormError errors={errors} fieldName='optInLegalCompetition' />
                            </div>
                            <div className={cx('mlb-field-wrap mlb-checkbox-wrap', errorClassName(errors, 'smsAllowed'))}>
                                <label className='mlb-checkbox-field' htmlFor='smsAllowed'>
                                    <input type='checkbox' {...register('smsAllowed')} id='smsAllowed' />
                                    <span className='mlb-checkmark' />
                                    {t('marketing_info_sms')}
                                </label>
                                <FormError errors={errors} fieldName='optInLegalCompetition' />
                            </div>
                            <div dangerouslySetInnerHTML={ {__html: t(`marketing_info_${snakeCase(type)}`)}} />

                            <div className='mlb-submit-wrap'>
                                <input className='mlb-btn-primary' type="submit" value={t('submit')} />
                            </div>
                        </div>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default RequestForm;
